import './App.scss';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useEffect, Suspense, lazy } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import Menu from '../Menu/menu';
import Footer from '../footer/footer';

// Eager load critical pages
import HomePage from '../HomePage/homePage';
import NotFound from '../404/404';

// Lazy load other pages for better performance
const About = lazy(() => import('../About/about'));
const PrivacyPolicy = lazy(() => import('../cookies/privacyPolicy'));
const OurLocation = lazy(() => import('../ourLocation/ourLocation'));
const Contact = lazy(() => import('../contact/contact'));
const Portfolio = lazy(() => import('../Portfolio/portfolio'));

// Lazy load concept pages
const AllConcepts = lazy(() => import('../concepts/allConcepts'));
const Kitchens = lazy(() => import('../concepts/Kitchens/kitchens'));
const Office = lazy(() => import('../concepts/Offices/offices'));
const LivingRooms = lazy(() => import('../concepts/LivingRooms/livingRooms'));
const Bedrooms = lazy(() => import('../concepts/Bedrooms/bedrooms'));
const Bathrooms = lazy(() => import('../concepts/Bathrooms/bathrooms'));
const InteriorDoors = lazy(() => import('../concepts/InteriorDoors/interiorDoors'));
const TableAndChairs = lazy(() => import('../concepts/TableChairs/tableChairs'));
const ClosetAndSlidingSystems = lazy(() => import('../concepts/Closet/closet'));
const HomeAccessories = lazy(() => import('../concepts/HomeAccessories/homeAccessories'));
const MaterialsFittingAndAccessories = lazy(() => import('../concepts/MaterialFitting/materialFitting'));
const InteriorElements = lazy(() => import('../concepts/InteriorElements/interiorElement'));
const HotelFurniture = lazy(() => import('../concepts/HotelFurniture/hotelFurniture'));

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

// Loading fallback component
function PageLoader() {
  return (
    <div className="page-loader">
      <div className="loader-content">
        <div className="loader-spinner" />
      </div>
    </div>
  );
}

// Animated page wrapper
// eslint-disable-next-line react/prop-types
function AnimatedPage({ children }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="animated-page"
    >
      {children}
    </motion.div>
  );
}

function App() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  return (
    <div className="App">
      <Menu />
      <Suspense fallback={<PageLoader />}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route
              path="/"
              element={(
                <AnimatedPage>
                  <HomePage />
                </AnimatedPage>
              )}
            />
            <Route
              path="/privacy-policy"
              element={(
                <AnimatedPage>
                  <PrivacyPolicy />
                </AnimatedPage>
              )}
            />
            <Route
              path="/about"
              element={(
                <AnimatedPage>
                  <About />
                </AnimatedPage>
              )}
            />
            <Route
              path="/location"
              element={(
                <AnimatedPage>
                  <OurLocation />
                </AnimatedPage>
              )}
            />
            <Route
              path="/contact"
              element={(
                <AnimatedPage>
                  <Contact />
                </AnimatedPage>
              )}
            />
            <Route
              path="/portfolio"
              element={(
                <AnimatedPage>
                  <Portfolio />
                </AnimatedPage>
              )}
            />
            <Route
              path="/concepts"
              element={(
                <AnimatedPage>
                  <AllConcepts />
                </AnimatedPage>
              )}
            />
            <Route
              path="/kitchens"
              element={(
                <AnimatedPage>
                  <Kitchens />
                </AnimatedPage>
              )}
            />
            <Route
              path="/office"
              element={(
                <AnimatedPage>
                  <Office />
                </AnimatedPage>
              )}
            />
            <Route
              path="/livingRooms"
              element={(
                <AnimatedPage>
                  <LivingRooms />
                </AnimatedPage>
              )}
            />
            <Route
              path="/bedrooms"
              element={(
                <AnimatedPage>
                  <Bedrooms />
                </AnimatedPage>
              )}
            />
            <Route
              path="/bathrooms"
              element={(
                <AnimatedPage>
                  <Bathrooms />
                </AnimatedPage>
              )}
            />
            <Route
              path="/interior-doors"
              element={(
                <AnimatedPage>
                  <InteriorDoors />
                </AnimatedPage>
              )}
            />
            <Route
              path="/table-and-chairs"
              element={(
                <AnimatedPage>
                  <TableAndChairs />
                </AnimatedPage>
              )}
            />
            <Route
              path="/closet-and-sliding-systems"
              element={(
                <AnimatedPage>
                  <ClosetAndSlidingSystems />
                </AnimatedPage>
              )}
            />
            <Route
              path="/home-accessories"
              element={(
                <AnimatedPage>
                  <HomeAccessories />
                </AnimatedPage>
              )}
            />
            <Route
              path="/materials-fitting-and-accessories"
              element={(
                <AnimatedPage>
                  <MaterialsFittingAndAccessories />
                </AnimatedPage>
              )}
            />
            <Route
              path="/interior-elements"
              element={(
                <AnimatedPage>
                  <InteriorElements />
                </AnimatedPage>
              )}
            />
            <Route
              path="/hotel-furniture"
              element={(
                <AnimatedPage>
                  <HotelFurniture />
                </AnimatedPage>
              )}
            />
            <Route
              path="*"
              element={(
                <AnimatedPage>
                  <NotFound />
                </AnimatedPage>
              )}
            />
          </Routes>
        </AnimatePresence>
      </Suspense>
      <Footer />
    </div>
  );
}

export default App;
